import {Component, inject, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {NgbActiveModal, NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {CompaniesApiService} from '@core/services/api/companies-api.service';
import {AlertService} from '@core/services/alert.service';
import {CpfCnpjPipe} from '@shared/pipes/app-cpfcnpj.pipe';
import {copyToClipboard} from "@shared/commons/keyboard.commons";
import {AppPhoneComponent} from "@shared/components/commons/app-phone/app-phone.component";
import {CompanyDebtsComponent} from "@features/companies/components/company-debts/company-debts.component";
import {PersonTypeHelper} from "@core/models/person-type.enums";

@Component({
    selector: 'app-company-details',
    standalone: true,
    imports: [CommonModule, CpfCnpjPipe, AppPhoneComponent],
    templateUrl: './company-details.component.html',
    styleUrl: './company-details.component.scss'
})
export class CompanyDetailsComponent implements OnInit {
    private alertService = inject(AlertService);
    private companiesApiService = inject(CompaniesApiService);
    private modalService = inject(NgbModal);
    activeModal = inject(NgbActiveModal);

    protected PersonTypeHelper = PersonTypeHelper;

    // Input from parent (modal opener)
    companyId!: number;

    company: any | null = null;

    ngOnInit(): void {
        if (this.companyId) {
            this.findById(this.companyId);
        }
    }

    protected enrichByDocumentNumber(documentNumber: string): void {
        if (!documentNumber) return;

        this.companiesApiService.enrich(documentNumber)
            .subscribe({
                next: () => {
                    this.alertService.successfulOperation();
                    this.findById(this.company.id);
                },
                error: (_error) => {
                    if (_error.error.code === 'CNPJWS_RATE_LIMIT') {
                        this.alertService.error("Ops", `Limite de consultas atingido, aguarde 5 minutos para tentar novamente.`);
                        return;
                    }

                    this.alertService.operationFailure()
                }
            })
    }

    private findById(companyId: number): void {
        this.companiesApiService.findById(companyId)
            .subscribe({
                next: (response) => {
                    this.company = response.data;
                },
                error: () => this.alertService.operationFailure()
            })
    }

    getFullAddress(): string {
        if (!this.company) return '—';

        const parts: string[] = [];

        if (this.company.addressType && this.company.streetName) {
            parts.push(`${this.company.addressType} ${this.company.streetName}`);
        } else if (this.company.streetName) {
            parts.push(this.company.streetName);
        }

        if (this.company.number) {
            parts.push(this.company.number);
        }

        if (this.company.complement && this.company.complement.trim()) {
            parts.push(this.company.complement);
        }

        if (this.company.neighborhood) {
            parts.push(this.company.neighborhood);
        }

        if (this.company.city && this.company.state) {
            parts.push(`${this.company.city}/${this.company.state}`);
        }

        if (this.company.zipcode) {
            parts.push(`CEP: ${this.company.zipcode}`);
        }

        return parts.length > 0 ? parts.join(', ') : '—';
    }


    goBack(): void {
        this.activeModal.close();
    }

    viewDebts(): void {
        if (this.company) {
            const modalRef = this.modalService.open(CompanyDebtsComponent, {
                size: 'fullscreen',
                centered: true,
                scrollable: true
            });

            modalRef.componentInstance.companyId = this.company.id;
        }
    }

    protected async copy(value: any) {
        await copyToClipboard(value);
        this.alertService.success('messages.success.label', 'messages.success.copy_to_clipboard');
    }

    protected goToSisparPage() {
        const url = 'https://sisparnet.pgfn.fazenda.gov.br/sisparInternet/internet/darf/consultaParcelamentoDarfInternet.xhtml';
        window.open(url, '_blank');
    }
}
